// src/components/chatUnreadStore.js
//
// Single source of truth for project-chat unread counts.
//
// IMPORTANT: getState() returns a CACHED snapshot object.
// The snapshot reference only changes when the store actually mutates.
// This is required by useSyncExternalStore — if getSnapshot() returns
// a new object reference every call, React goes into an infinite loop.

const _s = {
  unreadCounts:  {},
  openProjectId: null,
  _subscribers:  [],
  _snapshot:     null,  // cached snapshot — only replaced on mutation
};

// Build a fresh snapshot and cache it
function _buildSnapshot() {
  _s._snapshot = {
    unreadCounts:  { ..._s.unreadCounts },
    openProjectId: _s.openProjectId,
  };
  return _s._snapshot;
}

// Notify all subscribers with the new snapshot
function _notify() {
  _buildSnapshot();
  _s._subscribers.forEach(fn => {
    try { fn(); } catch (e) { console.error('chatUnreadStore:', e); }
  });
}

// ── READ ──────────────────────────────────────────────────
// Returns the cached snapshot — same reference if nothing changed.
// useSyncExternalStore calls this on every render to check if update needed.
export function getState() {
  if (!_s._snapshot) _buildSnapshot();
  return _s._snapshot;
}

export function getCounts()        { return _s._snapshot ? { ..._s._snapshot.unreadCounts } : {}; }
export function getOpenProjectId() { return _s.openProjectId; }

// ── WRITE ─────────────────────────────────────────────────
export function incrementUnread(projectId, hasMention = false) {
  const key = String(projectId);
  if (_s.openProjectId === key) return;
  const prev = _s.unreadCounts[key] || { count: 0, hasMention: false };
  _s.unreadCounts = {
    ..._s.unreadCounts,
    [key]: { count: prev.count + 1, hasMention: prev.hasMention || hasMention },
  };
  _notify();
}

export function seedCounts(baseline) {
  if (!baseline || typeof baseline !== 'object') return;
  _s.unreadCounts = { ...baseline };
  _notify();
}

export function clearProject(projectId) {
  const key = String(projectId);
  if (!_s.unreadCounts[key]) return;
  const { [key]: _removed, ...rest } = _s.unreadCounts;
  _s.unreadCounts = rest;
  _notify();
}

export function setOpenProject(projectId) {
  _s.openProjectId = projectId ? String(projectId) : null;
  if (_s.openProjectId) {
    // Clear count without calling clearProject (avoids double notify)
    if (_s.unreadCounts[_s.openProjectId]) {
      const { [_s.openProjectId]: _removed, ...rest } = _s.unreadCounts;
      _s.unreadCounts = rest;
    }
  }
  _notify();
}

export function setClosedProject() {
  _s.openProjectId = null;
  _notify();
}

export const clearOpenProject = setClosedProject;

// ── SUBSCRIBE ─────────────────────────────────────────────
// useSyncExternalStore calls subscribe(callback) and expects
// a cleanup function back. The callback is called with NO args —
// useSyncExternalStore then calls getSnapshot() itself to check
// if the snapshot reference changed.
export function subscribe(callback) {
  _s._subscribers.push(callback);
  return () => {
    _s._subscribers = _s._subscribers.filter(fn => fn !== callback);
  };
}

// ── LEGACY OBJECT EXPORT ──────────────────────────────────
// For any code that still imports chatUnreadStore.someMethod()
export const chatUnreadStore = {
  getState, getCounts, getOpenProjectId,
  incrementUnread, seedCounts,
  clearProject, setOpenProject, setClosedProject, clearOpenProject,
  subscribe,
};